import { useContext, type ReactNode } from "react";
import { styled } from "@linaria/react";
import { useLocation, useParams } from "react-router";
import { GAME_LIST, type GameId } from "../../games-list";
import { getGameContext, sameSchemaPage } from "../../data/derived";
import { buildHash } from "../../utils/filtering";
import { searchForSection, type SearchMode } from "../../utils/section-search";
import { AppContext } from "../AppContext";
import { GameIcon, KindIcon, SpriteIcon, UiIcon } from "../kind-icon/KindIcon";
import { Link } from "../Link";
import {
  DeclarationsContext,
  consolePath,
  schemaPath,
  sectionPath,
} from "../schema/DeclarationsContext";
import { SearchContext } from "../search/SearchContext";
import { OtherSectionContext, SECTIONS, sectionTotal } from "../search/useOtherSection";
import { SidebarCount } from "./Sidebar";
import { currentRow, iconButton, sidebarRow } from "./sidebar-styles";

/**
 * Brand, then where you are: the game and the section, each a list of rows. The page's own
 * list follows below it
 */
export function SidebarTop({
  section,
  count,
  onNavigate,
  sidebarOpen,
  show,
}: {
  section: SearchMode;
  count?: string;
  /** Closes the mobile drawer */
  onNavigate: () => void;
  sidebarOpen: boolean;
  /** The page's filters of what its list shows, a ShowGroup */
  show?: ReactNode;
}) {
  return (
    <Top>
      <BrandRow>
        <Brand href="https://s2v.app/">
          <SpriteIcon id="s2v-logo" size={28} />
          <span>Source 2 Viewer</span>
        </Brand>
        <ThemeButton />
        {sidebarOpen && (
          <IconButton onClick={onNavigate} aria-label="Close sidebar">
            <UiIcon name="close" />
          </IconButton>
        )}
      </BrandRow>
      <GameRows section={section} onNavigate={onNavigate} />
      <SectionRows section={section} count={count} onNavigate={onNavigate} />
      {show}
    </Top>
  );
}

function ThemeButton() {
  const { darkmode, setDarkmode } = useContext(AppContext);

  return (
    <ThemeToggle
      onClick={() => setDarkmode(!darkmode)}
      title={darkmode ? "Switch to light theme" : "Switch to dark theme"}
      aria-label="Toggle dark theme"
    >
      <UiIcon className="moon" name="moon" />
      <UiIcon className="sun" name="sun" />
    </ThemeToggle>
  );
}

function GameRows({ section, onNavigate }: { section: SearchMode; onNavigate: () => void }) {
  const { game } = useContext(DeclarationsContext);
  const { search } = useContext(SearchContext);
  const { hash } = useLocation();
  const { module, scope } = useParams();

  // The same page in the other game where it can be. A schema page missing there goes to its
  // module or the game's start. The page itself would send an unknown name to a game that
  // has it. Off the console page for a game without convars, only the search words that schemas
  // understand carry over
  function target(gameId: GameId) {
    const context = getGameContext(gameId);
    if (section === "schemas") {
      const page = sameSchemaPage(context.declarations, module, scope);
      return { pathname: schemaPath(gameId, page.module, page.scope), hash };
    }
    if (context.consoleItems.length > 0) {
      return { pathname: consolePath(gameId), hash };
    }
    return {
      pathname: schemaPath(gameId),
      hash: buildHash({ search: searchForSection(search, "schemas").search }),
    };
  }

  return (
    <Group aria-label="Games">
      <GroupLabel>Game</GroupLabel>
      {GAME_LIST.map((g) => (
        <NavRow
          key={g.id}
          to={target(g.id)}
          onClick={onNavigate}
          data-current={g.id === game || undefined}
          aria-current={g.id === game ? "true" : undefined}
        >
          <GameIcon game={g.id} />
          {g.name}
        </NavRow>
      ))}
    </Group>
  );
}

/** Filters of what the page's list shows. They sit under the sections and look like them */
export function ShowGroup({ children }: { children: ReactNode }) {
  return (
    <ShowRows aria-label="Show">
      <GroupLabel>Show</GroupLabel>
      {children}
    </ShowRows>
  );
}

/** A show filter, current like a section when it's on */
export const ShowRow = styled.button`
  ${sidebarRow}

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &[aria-pressed="true"] {
    ${currentRow}
  }
`;

/** Takes the place of an icon */
export const ShowRowSpacer = styled.span`
  width: 16px;
  flex-shrink: 0;
`;

function SectionRows({
  section,
  count,
  onNavigate,
}: {
  section: SearchMode;
  count?: string;
  onNavigate: () => void;
}) {
  const context = useContext(DeclarationsContext);
  const { search } = useContext(SearchContext);
  const other = useContext(OtherSectionContext)!;
  if (context.consoleItems.length === 0) return null;

  // Each section's results for the search, or all it has without one. The other section has
  // none when the search uses filters only this one has
  const countText = (mode: SearchMode) => {
    if (mode === section) return count ?? sectionTotal(context, mode).toLocaleString("en-US");
    if (!search) return sectionTotal(context, mode).toLocaleString("en-US");
    return other.count !== null && other.count.toLocaleString("en-US");
  };

  // The current section goes back to its start. The other section takes the search with it
  const row = (mode: SearchMode) => {
    const current = mode === section;
    const text = countText(mode);
    return (
      <NavRow
        key={mode}
        to={current ? sectionPath(context.game, mode) : other.to}
        onClick={onNavigate}
        data-current={current || undefined}
        aria-current={current ? "true" : undefined}
      >
        <KindIcon kind={SECTIONS[mode].icon} />
        {SECTIONS[mode].label}
        {text && <SidebarCount>{text}</SidebarCount>}
      </NavRow>
    );
  };

  return (
    <Group aria-label="Sections">
      <GroupLabel>Section</GroupLabel>
      {row("schemas")}
      {row("console")}
    </Group>
  );
}

const Top = styled.div`
  flex-shrink: 0;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--row-line);
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  /* Centered on the navbar's search box next to it */
  height: 38px;
  margin: 4px 0 2px;
`;

const Brand = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 16px;
  text-decoration: none;
  color: var(--text);
  white-space: nowrap;
`;

const IconButton = styled.button`
  ${iconButton}
`;

// Pushes itself and the close button after it to the end of the row. The icon follows the theme
// through CSS, because the prerendered page can't know it
const ThemeToggle = styled.button`
  ${iconButton}
  margin-left: auto;

  [data-theme="dark"] & > .sun,
  :root:not([data-theme="dark"]) & > .moon {
    display: none;
  }
`;

const Group = styled.nav`
  display: flex;
  flex-direction: column;
`;

// A fieldset groups the filters for assistive tech, without its default frame
const ShowRows = styled.fieldset`
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
`;

// Lines up with the rows' text past their icon, like the list's group headers past their chevron
const GroupLabel = styled.div`
  padding: 8px 10px 4px 32px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-dim);
`;

const NavRow = styled(Link)`
  ${sidebarRow}

  &[data-current] {
    ${currentRow}
  }
`;
